import { BaseHost } from "@core/shared/office-hosts/base-host";
import { IOfficeHost } from "@core/shared/office-hosts/office-host.types";
import { logger } from "@services/logger/logger.service";
import * as cmmn from '@utils/index';

export class PowerPointHost extends BaseHost implements IOfficeHost
{
    private static _textShapeTypes?: Set<PowerPoint.Shape[ 'type' ]>;
    private static _textPlaceholderTypes?: Set<PowerPoint.PlaceholderFormat[ 'type' ]>;

    private static get TEXT_SHAPE_TYPES(): Set<PowerPoint.Shape[ 'type' ]>
    {
        return this._textShapeTypes ??= new Set<PowerPoint.Shape[ 'type' ]>([
            PowerPoint.ShapeType.textBox,
            PowerPoint.ShapeType.geometricShape,
            PowerPoint.ShapeType.callout,
        ]);
    }

    private static get TEXT_PLACEHOLDER_TYPES(): Set<PowerPoint.PlaceholderFormat[ 'type' ]>
    {
        return this._textPlaceholderTypes ??= new Set<PowerPoint.PlaceholderFormat[ 'type' ]>([
            PowerPoint.PlaceholderType.date,
            PowerPoint.PlaceholderType.slideNumber,
            PowerPoint.PlaceholderType.footer,
            PowerPoint.PlaceholderType.header,
            PowerPoint.PlaceholderType.title,
            PowerPoint.PlaceholderType.body,
            PowerPoint.PlaceholderType.centerTitle,
            PowerPoint.PlaceholderType.subtitle,
            PowerPoint.PlaceholderType.verticalTitle,
            PowerPoint.PlaceholderType.verticalBody,
            PowerPoint.PlaceholderType.content,
            PowerPoint.PlaceholderType.verticalContent,
        ]);
    }

    public override async getDocumentIDAsync(): Promise<string>
    {
        const fileUrlID = await this.resolveFileUrlDocumentIDAsync();
        if (fileUrlID)
        {
            return fileUrlID;
        }

        return (await this.resolveSettingsDocumentIDAsync()) ?? '';
    }

    public override checkSelectable() { return true; }
    public override checkEditable(): boolean { return true; }
    public override checkDocumentReadable(): boolean { return true; }

    public override getTag(): string
    {
        return 'powerpoint';
    }

    public override async getDocumentTextAsync(): Promise<string>
    {
        const selectedSlideIndex = await this.getSelectedSlideIndex();

        try
        {
            return await PowerPoint.run(async (context) =>
            {
                const slide = context.presentation.slides.getItemAt(selectedSlideIndex);
                const shapes = slide.shapes;
                shapes.load('items/type');
                await context.sync();

                const supportsTables = Office.context.requirements.isSetSupported('PowerPointApi', '1.8');
                const placeholderShapes = supportsTables
                    ? shapes.items.filter((shape) => shape.type === PowerPoint.ShapeType.placeholder)
                    : [];
                for (const shape of placeholderShapes)
                {
                    shape.placeholderFormat.load('type,containedType');
                }
                if (placeholderShapes.length)
                {
                    await context.sync();
                }

                const tableShapes = supportsTables
                    ? shapes.items.filter((shape) => this.isTableShape(shape))
                    : [];
                const tables = tableShapes.map((shape) => shape.getTable());
                for (const table of tables)
                {
                    table.load('values');
                }
                if (tables.length)
                {
                    await context.sync();
                }

                const slideTexts = tables
                    .map((table) => table.values.map((row) => row.join('\t')).join('\n'))
                    .filter(Boolean);
                const textShapes = shapes.items.filter((shape) => this.isTextShape(shape, supportsTables));
                try
                {
                    for (const shape of textShapes)
                    {
                        shape.textFrame.load({
                            hasText: true,
                            textRange: { text: true },
                        });
                    }
                    await context.sync();

                    const shapeTexts = textShapes
                        .map((shape) => shape.textFrame)
                        .filter((textFrame) => textFrame.hasText)
                        .map((textFrame) => textFrame.textRange.text)
                        .filter((text) => text);
                    slideTexts.unshift(...shapeTexts);
                } catch (error)
                {
                    logger.error(this, `getDocumentTextAsync() > failed to read shape text: ${ (error as Office.Error).message }`);
                    if (!slideTexts.length)
                    {
                        throw error;
                    }
                }

                return slideTexts.join('\n');
            });
        } catch (error)
        {
            logger.error(this, `getDocumentTextAsync() > failed: ${ (error as Office.Error).message }`);
            return '';
        }
    }

    public override async getDocumentImageAsync(): Promise<string>
    {
        if (!Office.context.requirements.isSetSupported('PowerPointApi', '1.8'))
        {
            return '';
        }

        const selectedSlideIndex = await this.getSelectedSlideIndex();

        try
        {
            return await PowerPoint.run(async (context) =>
            {
                const slide = context.presentation.slides.getItemAt(selectedSlideIndex);
                const image = slide.getImageAsBase64({ width: 1920 });
                await context.sync();
                return image.value;
            });
        } catch (error)
        {
            logger.error(this, `getDocumentImageAsync() > failed: ${ (error as Office.Error).message }`);
            return '';
        }
    }

    private isTableShape(shape: PowerPoint.Shape): boolean
    {
        return shape.type === PowerPoint.ShapeType.table
            || (
                shape.type === PowerPoint.ShapeType.placeholder
                && shape.placeholderFormat.containedType === PowerPoint.ShapeType.table
            );
    }

    private isTextShape(shape: PowerPoint.Shape, supportsPlaceholders: boolean): boolean
    {
        return PowerPointHost.TEXT_SHAPE_TYPES.has(shape.type)
            || (
                supportsPlaceholders
                && shape.type === PowerPoint.ShapeType.placeholder
                && PowerPointHost.TEXT_PLACEHOLDER_TYPES.has(shape.placeholderFormat.type)
                && (
                    shape.placeholderFormat.containedType === null
                    || PowerPointHost.TEXT_SHAPE_TYPES.has(shape.placeholderFormat.containedType)
                )
            );
    }

    private async getSelectedSlideIndex(): Promise<number>
    {
        return await cmmn.callAsync<number>(async (r) =>
        {
            Office.context.document.getSelectedDataAsync<{ slides: { index: number; }[]; }>(
                Office.CoercionType.SlideRange,
                (result) =>
                {
                    if (result.status == Office.AsyncResultStatus.Failed || !result.value?.slides?.length)
                    {
                        return r.next(0);
                    }
                    // SlideRange index is 1-based; slides collection is 0-based.
                    r.next(Math.max(0, result.value.slides[ 0 ].index - 1));
                }
            );
        });
    }

    public override async getSelectedContentAsync(): Promise<string>
    {
        let resultStr = '';

        const resultValue = await cmmn.callAsync<boolean>(async (r) =>
        {
            Office.context.document.getSelectedDataAsync<string>(Office.CoercionType.Text, (getResult) =>
            {
                if (getResult.status == Office.AsyncResultStatus.Failed)
                {
                    logger.error("error: " + getResult.error.name + ", " + getResult.error.message);
                    return r.next(false);
                }

                resultStr = getResult.value;
                return r.next(true);
            });
        });

        if (resultValue)
        {
            return resultStr;
        }

        return '';
    }

    public override async setSelectedTextAsync(plainText: string): Promise<void>
    {
        await this.writeSelectedDataAsync(plainText, { coercionType: Office.CoercionType.Text });
    }

    public override async setSelectedImageAsync(fileName: string, b64Text: string): Promise<void>
    {
        const range = await new Promise<Office.SlideRange>((resolve, reject) =>
        {
            Office.context.document.getSelectedDataAsync(Office.CoercionType.SlideRange, (result) =>
            {
                if (result.status === Office.AsyncResultStatus.Failed)
                {
                    reject(new Error(`setSelectedImageAsync() > read slide: ${ result.error.message }`));
                }
                else
                {
                    resolve(result.value);
                }
            });
        });
        const slide = range?.slides?.[ 0 ];
        if (!slide)
        {
            throw new Error('setSelectedImageAsync() > No slide is selected');
        }

        await new Promise<void>((resolve, reject) =>
        {
            Office.context.document.goToByIdAsync(
                slide.id,
                Office.GoToType.Slide,
                { selectionMode: Office.SelectionMode.None },
                (result) => result.status === Office.AsyncResultStatus.Failed
                    ? reject(new Error(`setSelectedImageAsync() > navigate to slide: ${ result.error.message }`))
                    : resolve(),
            );
        });
        await this.writeSelectedDataAsync(b64Text, {
            coercionType: Office.CoercionType.Image,
            imageLeft: 0,
            imageTop: 0,
        });
    }

    private writeSelectedDataAsync(data: string, options: Office.SetSelectedDataOptions): Promise<void>
    {
        return new Promise<void>((resolve, reject) =>
        {
            Office.context.document.setSelectedDataAsync(
                data,
                options,
                (result) => result.status === Office.AsyncResultStatus.Failed
                    ? reject(new Error(`PowerPoint ${ options.coercionType } insertion failed: ${ result.error.message }`))
                    : resolve(),
            );
        });
    }
}
